import React from "react";
import { Accordion, AccordionDetails, AccordionSummary, Table, TableBody, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ClearIcon from "@mui/icons-material/Clear";
import CheckIcon from "@mui/icons-material/Check";
import PropTypes from "prop-types";
import { EventContentTableRow } from "../EventContentTableRow";
import formatter from "app/utils/formatter";
import renderers from "./renderers";
const EventDifferencesContext = React.createContext(null);
export const useEventDifferences = () => {
  return React.useContext(EventDifferencesContext);
};
const EventContentTable = ({ event, ignoreKeys, orderKeys, keyRenderers, collapse }) => {
  const [expanded, setExpanded] = React.useState(false);
  const { type, previousEventDifferences } = event;
  const contextDifferences = useEventDifferences();
  const activeDifferences = previousEventDifferences || contextDifferences;
  const hasKeyChanged = React.useCallback(
    (key) => {
      if (!activeDifferences) return false;
      return Object.keys(activeDifferences).some((diffKey) => diffKey.endsWith(`.${key}`) || diffKey === key);
    },
    [activeDifferences]
  );
  const filteredKeys = React.useMemo(() => {
    const allKeys = Object.keys(event).filter((key) => !ignoreKeys.includes(key));
    const validOrderKeys = orderKeys || [];
    const orderedKeys = validOrderKeys.filter((key) => allKeys.includes(key));
    const remainingKeys = allKeys.filter((key) => !validOrderKeys.includes(key));
    return [...orderedKeys, ...remainingKeys];
  }, [event, ignoreKeys, orderKeys]);
  const renderEventKey = React.useCallback(
    (key) => {
      const renderer = key === "value" && type === "wage" ? keyRenderers.wage : keyRenderers[key];
      const hasChanged = hasKeyChanged(key);
      return /* @__PURE__ */ React.createElement(EventContentTableRow, { key, header: key, value: event[key], renderer, hasChanged });
    },
    [event, type, keyRenderers, hasKeyChanged]
  );
  const tableContent = React.useMemo(
    () => /* @__PURE__ */ React.createElement(Table, null, /* @__PURE__ */ React.createElement(TableBody, null, filteredKeys.map(renderEventKey))),
    [filteredKeys, renderEventKey]
  );
  const accordionContent = React.useMemo(
    () => /* @__PURE__ */ React.createElement(Accordion, { expanded, onChange: () => setExpanded(!expanded) }, /* @__PURE__ */ React.createElement(AccordionSummary, { expandIcon: /* @__PURE__ */ React.createElement(ExpandMoreIcon, null) }), /* @__PURE__ */ React.createElement(AccordionDetails, null, tableContent)),
    [expanded, tableContent]
  );
  const content = collapse ? accordionContent : tableContent;
  if (previousEventDifferences && !contextDifferences) {
    return /* @__PURE__ */ React.createElement(EventDifferencesContext.Provider, { value: previousEventDifferences }, content);
  }
  return content;
};
EventContentTable.propTypes = {
  event: PropTypes.object.isRequired,
  keyRenderers: PropTypes.object,
  collapse: PropTypes.bool,
  ignoreKeys: PropTypes.array,
  orderKeys: PropTypes.array
};
EventContentTable.defaultProps = {
  collapse: false,
  ignoreKeys: [
    "@id",
    "mongoId",
    "connectionId",
    "@type",
    "eventType",
    "previousEventDifferences",
    "hrEmployee",
    "batch",
    "batchId",
    "externalId"
  ],
  orderKeys: ["employeeName", "eventDate", "coverage", "processedYear", "processedMonth"],
  keyRenderers: {
    correction: formatter.yesNoEmpty,
    wage: formatter.currency,
    annualWage: formatter.currency,
    grossYearlyFullTimeComponentWage: formatter.currency,
    grossYearlyFullTimeComponentWagePrevious: formatter.currency,
    hrEmployee: (value) => /* @__PURE__ */ React.createElement("span", null, `${value.personName.fullName} ${value.syncStatus && `(${value.syncStatus})`}`),
    processed: (value) => value ? /* @__PURE__ */ React.createElement(CheckIcon, null) : /* @__PURE__ */ React.createElement(ClearIcon, null),
    archived: (value) => value ? /* @__PURE__ */ React.createElement(CheckIcon, null) : /* @__PURE__ */ React.createElement(ClearIcon, null),
    periodStart: formatter.date,
    periodEnd: formatter.date,
    periodWage: formatter.currency,
    employeeCategory: renderers.employeeCategory,
    hourlyWage: formatter.currency,
    cumulativeWage: formatter.currency,
    createdAt: formatter.dateTime,
    eventDate: formatter.systemDate,
    startDate: formatter.systemDate,
    dateStart: formatter.date,
    endDate: formatter.systemDate,
    dateOfBirth: formatter.date,
    startDateOfEmployment: formatter.date,
    endDateOfEmployment: formatter.date,
    employmentStart: formatter.systemDate,
    employmentEnd: formatter.systemDate,
    probationDate: formatter.date,
    percentageStartDate: formatter.date,
    relationStartDate: formatter.date,
    relationEndDate: formatter.date,
    grossWage: formatter.currency,
    svWage: formatter.currency,
    aofWkoPremium: formatter.currency,
    children: (value) => value.length > 0 && value.map((child) => /* @__PURE__ */ React.createElement(EventContentTable, { key: child["@id"], event: child, ignoreKeys: ["@key", "@type", "@id"], collapse: true })),
    address: (value) => value && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Typography, { component: "p" }, /* @__PURE__ */ React.createElement("span", null, `${value.street} ${value.houseNumber}`), value.houseNumberSuffix && /* @__PURE__ */ React.createElement("span", null, ` ${value.houseNumberSuffix}`)), /* @__PURE__ */ React.createElement(Typography, { component: "p" }, `${value.zipCode} ${value.city}`), /* @__PURE__ */ React.createElement(Typography, null, value.country)),
    previousAnnualWage: formatter.currency,
    previousDateStart: formatter.date,
    eventSource: renderers.eventSource
  }
};
export default EventContentTable;
